import { useMemo, useState } from 'react';
import { KiteProgressIndicator, KiteIcon } from '@kite/react-kite';
import {
  GeographyStackedBarChart,
  BusinessPieChart,
  PrintCountComposedChart,
} from './charts';

import useFetchAnalytics from 'hooks/apiCalls/useFetchAnalytics';

import './Analytics.scss';

interface IAnalyticsProps {
  collateralId?: string;
  className?: string;
}

interface ITableRow {
  name: string;
  count: number;
}

type TPrintPeriod = 'daily' | 'weekly' | 'monthly';

interface IPrintCountChartRow {
  label: string;
  successful: number;
  failed: number;
  trendline: number;
}

const PIE_COLORS = ['#1c6fe6', '#081f5f'];
const BAR_SUCCESS_COLOR = '#1c6fe6';
const BAR_FAILED_COLOR = '#081f5f';
const TRENDLINE_COLOR = '#f6b71e';

const EMPTY_LABEL = 'Unspecified';

const getDisplayLabel = (value: string) => {
  const trimmedValue = value.trim();

  if (!trimmedValue || trimmedValue.toLowerCase() === 'null') {
    return EMPTY_LABEL;
  }

  if (trimmedValue.toLowerCase() === 'default') {
    return 'Default';
  }

  const withoutHubSuffix = trimmedValue.replace(/\s+HUB$/i, '');

  return withoutHubSuffix
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');
};

const normalizeDelimitedText = (value: string | undefined) => {
  if (!value) {
    return '';
  }

  return value.replace(/\s*;\s*/g, ', ');
};

const getPrintCountChartData = (
  selectedPeriod: TPrintPeriod,
  analyticsData: ReturnType<typeof useFetchAnalytics>['data']
): IPrintCountChartRow[] => {
  if (!analyticsData?.printCount) {
    return [];
  }

  if (selectedPeriod === 'daily') {
    return (analyticsData.printCount.daily || [])
      .slice()
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((item) => ({
        label: new Date(item.date).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: '2-digit',
        }),
        successful: item.success,
        failed: item.failed,
        trendline: item.success + item.failed,
      }));
  }

  if (selectedPeriod === 'weekly') {
    return (analyticsData.printCount.weekly || []).map((item) => ({
      label: item.week,
      successful: item.success,
      failed: item.failed,
      trendline: item.success + item.failed,
    }));
  }

  return (analyticsData.printCount.monthly || []).map((item) => ({
    label: item.month,
    successful: item.success,
    failed: item.failed,
    trendline: item.success + item.failed,
  }));
};

const Analytics = ({ collateralId, className = '' }: IAnalyticsProps) => {
  const { data: analyticsData, isLoading: isAnalyticsLoading } =
    useFetchAnalytics(collateralId);

  const [selectedPrintPeriod, setSelectedPrintPeriod] =
    useState<TPrintPeriod>('weekly');

  const totalPrintVolume = analyticsData?.summary?.totalPrintVolume || 0;
  const totalOrdersPrinted = analyticsData?.summary?.totalOrdersPrinted || 0;
  const savingsToDate = analyticsData?.summary?.podSavingsToDate || 0;

  const geographyChartData = (analyticsData?.printVolumeByHub || []).map(
    (item) => ({
      hub: getDisplayLabel(item.hub),
      successful: item.success,
      failed: item.failed,
      total: item.total,
    })
  );
  const geographyYAxisMax = Math.max(
    100,
    Math.ceil(
      (Math.max(...geographyChartData.map((item) => item.total), 0) * 1.1) /
        1000
    ) * 1000
  );

  const orderTypeRows: ITableRow[] = (analyticsData?.topOrderTypes || []).map(
    (item) => ({
      name: item.orderType,
      count: item.count,
    })
  );

  const equipmentRows: ITableRow[] = (
    analyticsData?.topEquipmentTypes || []
  ).map((item) => ({
    name: item.equipmentType,
    count: item.count,
  }));

  const lineOfBusinessData: ITableRow[] = (analyticsData?.lob || []).map(
    (item) => ({
      name: normalizeDelimitedText(item.lineOfBusiness),
      count: item.count,
    })
  );

  const printCountChartData = useMemo(
    () => getPrintCountChartData(selectedPrintPeriod, analyticsData),
    [selectedPrintPeriod, analyticsData]
  );

  const printCountSubtitle =
    selectedPrintPeriod.charAt(0).toUpperCase() + selectedPrintPeriod.slice(1);

  return (
    <div className={`analytics ${className}`}>
      {isAnalyticsLoading ? (
        <KiteProgressIndicator
          id="analytics-loading"
          message="Loading analytics..."
          title="Loading"
        />
      ) : analyticsData ? (
        <div className="analytics-dashboard">
          <div className="summary-cards">
            {/* <article className="summary-card">
              <div className="summary-card-copy">
                <h4>PoD Savings To Date</h4>
                <p>
                  $
                  {savingsToDate.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
              <div className="summary-card-icon" aria-hidden="true">
                <img src="/icons/summary-dollar.svg" alt="" />
              </div>
            </article> */}
            <article className="summary-card">
              <div className="summary-card-copy">
                <h4>Total Print Volume</h4>
                <p>{totalPrintVolume.toLocaleString()}</p>
              </div>
              <div className="summary-card-icon" aria-hidden="true">
                <img src="/icons/summary-file.svg" alt="" />
              </div>
            </article>
            <article className="summary-card">
              <div className="summary-card-copy">
                <h4>Total Orders Printed</h4>
                <p>{totalOrdersPrinted.toLocaleString()}</p>
              </div>
              <div className="summary-card-icon" aria-hidden="true">
                <img src="/icons/summary-print.svg" alt="" />
              </div>
            </article>
          </div>

          <div className="filter-row">
            <span className="filter-title">FILTER BY:</span>
            <button
              type="button"
              className="filter-chip"
              aria-label="Date range filter"
            >
              Date Range: All
              <KiteIcon icon="ki-chevron-down" />
            </button>
            <button type="button" className="filter-reset-btn">
              Reset
            </button>
          </div>

          <section className="panel panel-large">
            <div className="panel-header">
              <h3>Print Volume by Geography</h3>
              <KiteIcon icon="ki-more-horizontal" />
            </div>
            <div className="panel-tabs panel-tabs-tight">
              <button type="button" className="tab-btn tab-btn-active">
                Hub
              </button>
              <button type="button" className="tab-btn">
                Region
              </button>
            </div>
            {geographyChartData.length > 0 ? (
              <div className="chart-container chart-geography">
                <GeographyStackedBarChart
                  data={geographyChartData}
                  yAxisMax={geographyYAxisMax}
                />
              </div>
            ) : (
              <div className="no-chart-data">
                <p>No geography print data available.</p>
              </div>
            )}
          </section>

          <div className="mid-panels">
            <section className="panel">
              <div className="panel-header">
                <div className="panel-header-title">
                  <img
                    src="/icons/frame-order-types.svg"
                    alt=""
                    className="panel-header-icon"
                  />
                  <div className="panel-header-copy">
                    <p>Print Volume</p>
                    <h3>Top 5 Order Types</h3>
                  </div>
                </div>
                <KiteIcon icon="ki-more-horizontal" />
              </div>
              <table
                className="mini-table"
                aria-label="Print Volume by Order Type"
              >
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Order Type</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {orderTypeRows.map((item, index) => (
                    <tr key={`${item.name}-${index}`}>
                      <td>{`#${index + 1}`}</td>
                      <td>{normalizeDelimitedText(item.name)}</td>
                      <td>{item.count.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* <button type="button" className="download-report-link">
                <KiteIcon icon="ki-download" />
                Download Full Report
              </button> */}
            </section>

            <section className="panel">
              <div className="panel-header">
                <div className="panel-header-title">
                  <img
                    src="/icons/frame-equipment-types.svg"
                    alt=""
                    className="panel-header-icon"
                  />
                  <div className="panel-header-copy">
                    <p>Print Volume</p>
                    <h3>Top 5 Equipment Types</h3>
                  </div>
                </div>
                <KiteIcon icon="ki-more-horizontal" />
              </div>
              <table
                className="mini-table"
                aria-label="Print Volume by Equipment"
              >
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Equipment</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {equipmentRows.map((item, index) => (
                    <tr key={`${item.name}-${index}`}>
                      <td>{`#${index + 1}`}</td>
                      <td>{normalizeDelimitedText(item.name)}</td>
                      <td>{item.count.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* <button type="button" className="download-report-link">
                <KiteIcon icon="ki-download" />
                Download Full Report
              </button> */}
            </section>

            <section className="panel">
              <div className="panel-header">
                <div className="panel-header-title">
                  <img
                    src="/icons/frame-line-of-business.svg"
                    alt=""
                    className="panel-header-icon"
                  />
                  <div className="panel-header-copy">
                    <p>Print Volume</p>
                    <h3>Line of Business</h3>
                  </div>
                </div>
                <KiteIcon icon="ki-more-horizontal" />
              </div>
              <div className="business-panel-body">
                <div className="chart-container chart-pie">
                  <BusinessPieChart
                    data={lineOfBusinessData}
                    colors={PIE_COLORS}
                  />
                </div>
                <table
                  className="mini-table"
                  aria-label="Line of business summary"
                >
                  <tbody>
                    {lineOfBusinessData.map((item) => (
                      <tr key={item.name}>
                        <td>{item.name}</td>
                        <td>{item.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          <section className="panel panel-large">
            <div className="panel-header">
              <h3>Print Count</h3>
              <KiteIcon icon="ki-more-horizontal" />
            </div>
            <div className="panel-tabs">
              <button
                type="button"
                className={`tab-btn ${
                  selectedPrintPeriod === 'daily' ? 'tab-btn-active' : ''
                }`}
                onClick={() => setSelectedPrintPeriod('daily')}
              >
                Daily
              </button>
              <button
                type="button"
                className={`tab-btn ${
                  selectedPrintPeriod === 'weekly' ? 'tab-btn-active' : ''
                }`}
                onClick={() => setSelectedPrintPeriod('weekly')}
              >
                Weekly
              </button>
              <button
                type="button"
                className={`tab-btn ${
                  selectedPrintPeriod === 'monthly' ? 'tab-btn-active' : ''
                }`}
                onClick={() => setSelectedPrintPeriod('monthly')}
              >
                Monthly
              </button>
            </div>
            <p className="panel-subtitle">{printCountSubtitle} print count</p>
            {printCountChartData.length > 0 ? (
              <div className="chart-container chart-print-count">
                <PrintCountComposedChart
                  data={printCountChartData}
                  successColor={BAR_SUCCESS_COLOR}
                  failedColor={BAR_FAILED_COLOR}
                  trendlineColor={TRENDLINE_COLOR}
                />
              </div>
            ) : (
              <div className="no-chart-data">
                <p>No print count data available for the selected period.</p>
              </div>
            )}
            <div className="chart-pagination-controls" aria-hidden="true">
              <button
                type="button"
                className="circle-icon-btn"
                disabled={selectedPrintPeriod === 'daily'}
                onClick={() => {
                  if (selectedPrintPeriod === 'monthly')
                    setSelectedPrintPeriod('weekly');
                  else if (selectedPrintPeriod === 'weekly')
                    setSelectedPrintPeriod('daily');
                }}
              >
                <KiteIcon icon="ki-chevron-left" />
              </button>
              <button
                type="button"
                className="circle-icon-btn"
                disabled={selectedPrintPeriod === 'monthly'}
                onClick={() => {
                  if (selectedPrintPeriod === 'daily')
                    setSelectedPrintPeriod('weekly');
                  else if (selectedPrintPeriod === 'weekly')
                    setSelectedPrintPeriod('monthly');
                }}
              >
                <KiteIcon icon="ki-chevron-right" />
              </button>
            </div>
          </section>
        </div>
      ) : (
        <div className="no-analytics">
          <p>No analytics data available for this collateral.</p>
        </div>
      )}
    </div>
  );
};

export default Analytics;
